import '@/styles/vendors/kendo.scss';
import '@/style.scss';
import '@/styles/variables.scss';
import '@/styles/overrides.scss';
import '@/axios-intercepter';

/* eslint-disable no-param-reassign */
import { createApp, provide } from 'vue';
import { createPinia } from 'pinia';
import piniaPersist from 'pinia-plugin-persist';
import { library } from '@fortawesome/fontawesome-svg-core';
import { defaultColors } from './lib/chart-helpers';
import { piniaDebounce } from './plugins/piniaPlugins';
import { fontAwesomeIcons, kendoIconOverrides } from '@/lib/icons';
import { clickOutside } from './lib/directives';

// Components to be exposed
import VueApexCharts from 'vue3-apexcharts';
import ConnxSuspense from '@/components/Atoms/ConnxSuspense.vue';
import ConnxHeaderPortlet from '@/components/Atoms/ConnxHeaderPortlet.vue';
import ConnxTFASettings from '@/views/ConnxTFASettings/ConnxTFASettings.vue';
import ConnxSecuityCodeInput from '@/components/Organisms/ConnxSecurityCodeInput.vue';
import ConnxFlashMessages from '@/components/Organisms/ConnxFlashMessages.vue';
import ConnxTFASetup from '@/views/ConnxTFASetup/ConnxTFASetup.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxTabs from '@/components/Molecules/ConnxTabs.vue';
import ConnxTab from '@/components/Molecules/ConnxTab.vue';
import ConnxEmployeeManagementActions from '@/views/Employee/ManagementActions/ConnxEmployeeManagementActions.vue';
import ConnxHrAdminPronouns from '@/views/HRAdmin/ConnxHrAdminPronouns.vue';
import ConnxHrAdminGenderIdentities from '@/views/HRAdmin/ConnxHrAdminGenderIdentities.vue';
import ConnxColourTheme from '@/views/Admin/Settings/ColourConfigeration/ConnxColourTheme.vue';
import ConnxHrAdminWorkforceAssistanceReports from '@/views/HRAdmin/ConnxHrAdminWorkforceAssistanceReports.vue';
import ConnxWelcomeDashboard from '@/views/Welcome/Dashboard';
import ConnxWelcomeDashboardWidgetLibrary from '@/views/Admin/Dashboard/WidgetLibrary';

import useUISessionStore from '@/stores/ui-session';
import useDeviceStore from '@/stores/device';
import fetchUISession from '@/lib/fetch-ui-session';
import deviceDetails from '@/lib/device-details';
import Device from '@/interfaces/Device';
import useEmitter from '@/composables/emitter';

declare global {
  interface Window {
    vueEmitter: any;
    Apex: any;
    StartTimer?: (delay: number) => void;
    ShowFavsDiv?: () => void;
    ShowHelpDiv?: () => void;
  }
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

window.Apex = {
  chart: {
    fontSize: '13px',
    fontFamily:
      'ConnXFont, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
    animations: {
      enabled: false,
      animateGradually: {
        enabled: false,
      },
      dynamicAnimation: {
        enabled: false,
      },
    },
    width: '100%',
    toolbar: {
      show: false,
    },
  },
  colors: defaultColors,
  xaxis: {
    labels: {
      show: true,
      rotate: -45, // Rotates labels to fit more text
      rotateAlways: false,
      hideOverlappingLabels: false,
      trim: true, // Trims long text with "..."
      minHeight: 60,
    },
  },
};

const pinia = createPinia();
pinia.use(piniaPersist);
pinia.use(piniaDebounce);
library.add(...fontAwesomeIcons);

window.vueEmitter = useEmitter();

const main = async () => {
  const elements = document.querySelectorAll('.mount-vue') as NodeListOf<HTMLElement>;
  if (elements.length) {
    const state = await fetchUISession();
    const device = deviceDetails() as Device;
    for (const el of elements) {
      createApp({
        name: 'main-app',
        setup() {
          provide('kendoIcons', kendoIconOverrides);
          const deviceStore = useDeviceStore();
          const uiSessionStore = useUISessionStore();
          deviceStore.initStore(device);
          uiSessionStore.updateState(state);
        },
      })
        .directive('click-outside', clickOutside)
        .component('vue-apex-charts', VueApexCharts)
        .component('connx-tab', ConnxTab)
        .component('connx-tabs', ConnxTabs)
        .component('connx-suspense', ConnxSuspense)
        .component('connx-button', ConnxButton)
        .component('connx-tfa-setup', ConnxTFASetup)
        .component('connx-tfa-settings', ConnxTFASettings)
        .component('connx-flash-messages', ConnxFlashMessages)
        .component('connx-portlet-header', ConnxHeaderPortlet)
        .component('connx-colour-theme', ConnxColourTheme)
        .component('connx-security-code-input', ConnxSecuityCodeInput)
        .component('connx-hr-admin-pronouns', ConnxHrAdminPronouns)
        .component('connx-hr-admin-gender-identities', ConnxHrAdminGenderIdentities)
        .component('connx-welcome-dashboard', ConnxWelcomeDashboard)
        .component('connx-welcome-dashboard-widget-library', ConnxWelcomeDashboardWidgetLibrary)
        .component('connx-employee-management-actions', ConnxEmployeeManagementActions)
        .component('connx-hr-admin-workforce-assistance-reports', ConnxHrAdminWorkforceAssistanceReports)
        .use(pinia)
        .mount(el);
    }
  }
};
if (document.readyState === 'interactive' || document.readyState === 'complete') {
  main();
} else {
  const readyStateEvent = () => {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
      main();
      document.removeEventListener('readystatechange', readyStateEvent);
    }
  };
  document.addEventListener('readystatechange', readyStateEvent);
}
